import dayjs from 'dayjs'
import type { DashboardRange, HandlingBucket, HandlingCell, HandlingKey } from '../types/dashboard'

export type Tone = 'good' | 'bad' | 'neutral'
export type BetterWhen = 'lower' | 'neutral'
export type TimedMetric = 'tta_avg_s' | 'talk_median_s' | 'held_pct' | 'hold_avg_s'

export interface Delta {
    label: string
    tone: Tone
}

export const LOW_SAMPLE_MIN = 5

export const DASHBOARD_RANGES = [
    { value: '24h', label: 'Last 24h', short: '24h', hours: 24, refreshMs: 30_000 },
    { value: '7d', label: 'Last 7 days', short: '7 days', hours: 24 * 7, refreshMs: 300_000 },
    { value: '30d', label: 'Last 30 days', short: '30 days', hours: 24 * 30, refreshMs: 300_000 },
] as const satisfies readonly { value: DashboardRange; label: string; short: string; hours: number; refreshMs: number }[]

export const HANDLING_BUCKETS: { key: HandlingBucket; label: string }[] = [
    { key: 'morning', label: '06–12' },
    { key: 'afternoon', label: '12–18' },
    { key: 'evening', label: '18–00' },
    { key: 'night', label: '00–06' },
]

export const TONE_BADGE: Record<Tone, string> = {
    good: 'bg-green-100 text-green-800',
    bad: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-700',
}

export const TONE_TEXT: Record<Tone, string> = {
    good: 'text-green-600',
    bad: 'text-red-600',
    neutral: 'text-gray-400',
}

export function parseRange(value: unknown): DashboardRange {
    return DASHBOARD_RANGES.find((range) => range.value === value)?.value ?? '24h'
}

export function rangeWindow(range: DashboardRange, now: Date = new Date()): { from: Date; to: Date } {
    const { hours } = DASHBOARD_RANGES.find((r) => r.value === range)!
    return { from: new Date(now.getTime() - hours * 3_600_000), to: now }
}

export interface StatsWindow {
    from: Date
    to: Date
    range: DashboardRange
}

export function windowLabel({ from, to, range }: StatsWindow): string {
    const format = (d: Date) => dayjs(d).format('D MMM HH:mm')
    const { short } = DASHBOARD_RANGES.find((r) => r.value === range)!
    return `${format(from)} → ${format(to)} · vs previous ${short}`
}

export function formatDuration(seconds: number | null | undefined): string {
    if (seconds == null) return '—'
    const total = Math.round(seconds)
    if (total < 60) return `${total}s`
    if (total < 3600) return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
    return `${Math.floor(total / 3600)}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`
}

const sign = (n: number) => (n > 0 ? '+' : '−')

function tone(diff: number, betterWhen: BetterWhen): Tone {
    if (betterWhen === 'neutral' || diff === 0) return 'neutral'
    return diff < 0 ? 'good' : 'bad'
}

export function countDelta(current: number, previous: number, mode: 'percent' | 'absolute', betterWhen: BetterWhen): Delta | null {
    if (current === 0 && previous === 0) return null
    const diff = current - previous
    if (diff === 0) return { label: 'no change', tone: 'neutral' }
    if (mode === 'absolute' || previous === 0) return { label: `${sign(diff)}${Math.abs(diff)}`, tone: tone(diff, betterWhen) }
    return { label: `${sign(diff)}${Math.abs(Math.round((100 * diff) / previous))}%`, tone: tone(diff, betterWhen) }
}

export function durationDelta(current: number | null, previous: number | null, betterWhen: BetterWhen): Delta | null {
    if (current == null || previous == null) return null
    const diff = Math.round(current - previous)
    if (diff === 0) return { label: 'no change', tone: 'neutral' }
    return { label: `${sign(diff)}${formatDuration(Math.abs(diff))} vs prev`, tone: tone(diff, betterWhen) }
}

export function pointsDelta(current: number | null, previous: number | null): Delta | null {
    if (current == null || previous == null) return null
    const diff = Math.round((current - previous) * 10) / 10
    if (diff === 0) return { label: 'no change', tone: 'neutral' }
    return { label: `${sign(diff)}${Math.abs(diff)} pts vs prev`, tone: tone(diff, 'lower') }
}

export function worstBucket(handling: Record<HandlingKey, HandlingCell>, metric: TimedMetric): HandlingBucket | null {
    const candidates = HANDLING_BUCKETS
        .map(({ key }) => ({ key, cell: handling[key] }))
        .filter(({ cell }) => cell.calls >= LOW_SAMPLE_MIN && cell[metric] != null)
    if (candidates.length < 2) return null
    return candidates.reduce((worst, c) => (c.cell[metric]! > worst.cell[metric]! ? c : worst)).key
}

export function createLatestGuard() {
    let latest = 0
    return {
        next: () => ++latest,
        isLatest: (id: number) => id === latest,
    }
}
